// CSV weather data parsing utilities - extracted from Forecast.vue

export interface TableRow {
  time: string
  temp: string          // rounded for table / icon display, e.g. "22℃"
  humidity: string      // rounded for table / icon display, e.g. "65%"
  wind: string
  cloud: string
  rain: string
  windSpeed?: string
  windDir?: string
  // Raw parsed numbers — chart view plots these so sub-degree / sub-percent
  // precision from the backend survives all the way through. NaN if the
  // source cell was missing or unparseable.
  tempRaw?: number
  humidityRaw?: number
}

// Module-level memoization cache
const csvFormatCache = new Map<string, TableRow[]>()

export function displayValue(value: string): string {
  if (!value || value.includes('N/A') || value.includes('NA')) return '-'
  return value
}

export function formatCsvForTable(csvData: string): TableRow[] {
  if (!csvData) return []
  if (csvFormatCache.has(csvData)) return csvFormatCache.get(csvData)!

  const lines = csvData.split('\n').filter(line => line.trim())
  if (lines.length === 0) return []

  const rows = lines.map(line => line.split(',').map(cell => cell.trim()))
  if (rows.length === 0) return []

  const times = rows[0].slice(1)
  const timeData: Record<string, TableRow & { windSpeed: string; windDir: string }> = {}

  times.forEach(time => {
    timeData[time] = {
      time, temp: '', humidity: '', wind: '', cloud: '', rain: '',
      windSpeed: '', windDir: '',
      tempRaw: NaN, humidityRaw: NaN,
    }
  })

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]
    if (row.length === 0) continue
    const paramName = row[0].toLowerCase()
    const values = row.slice(1)

    values.forEach((value, index) => {
      const time = times[index]
      if (!time || !timeData[time]) return

      if (paramName.includes('temperature')) {
        // Backend now emits 1 decimal (e.g. "22.3"); keep the raw number for
        // the chart and round the display string so the table stays tidy.
        const raw = parseFloat(value)
        timeData[time].tempRaw = raw
        timeData[time].temp = Number.isFinite(raw) ? `${Math.round(raw)}℃` : value + '℃'
      } else if (paramName.includes('humidity')) {
        const raw = parseFloat(value)
        timeData[time].humidityRaw = raw
        timeData[time].humidity = Number.isFinite(raw) ? `${Math.round(raw)}%` : value + '%'
      } else if (paramName.includes('wind speed') || paramName.includes('beaufort')) {
        timeData[time].windSpeed = value
        timeData[time].wind = timeData[time].windDir ? `${timeData[time].windDir} ${value}` : value
      } else if (paramName.includes('wind direction')) {
        timeData[time].windDir = value
        timeData[time].wind = timeData[time].windSpeed ? `${value} ${timeData[time].windSpeed}` : value
      } else if (paramName.includes('cloud')) {
        timeData[time].cloud = value + '/8'
      } else if (paramName.includes('precipitation')) {
        timeData[time].rain = value
      }
    })
  }

  const tableData: TableRow[] = Object.values(timeData).map(row => ({
    time: row.time,
    temp: row.temp,
    humidity: row.humidity,
    wind: row.wind,
    cloud: row.cloud,
    rain: row.rain,
    windSpeed: row.windSpeed,
    windDir: row.windDir,
    tempRaw: row.tempRaw,
    humidityRaw: row.humidityRaw,
  }))

  csvFormatCache.set(csvData, tableData)
  if (csvFormatCache.size > 20) {
    csvFormatCache.delete(csvFormatCache.keys().next().value!)
  }

  return tableData
}

export function clearCsvCache(): void {
  csvFormatCache.clear()
}
